// Voyage identity presentation composer (P0-3 · 2026-02-11).
//
// The Cruise Selector is the sole traveller-facing owner of "which
// voyage am I looking at?". Heroes own "what workspace am I in?".
// Every traveller-visible label about a voyage flows through this
// helper so:
//   - internal identifiers (`voy_*`, UUIDs, ObjectIds) never surface,
//   - test sentinels (`CERT-*`, `TEST-*`) never surface even if they
//     leak into `cruises.name`, and
//   - dates use the canonical calendar-date semantics from PD-031.
//
// This module is presentation-only. It never mutates its inputs,
// never touches internal IDs, and has no side effects.

import { formatCalendarDate, formatCalendarRange, toCalendarDate } from "./calendarDate";

// Defensively strip trailing test/sentinel tokens that occasionally
// leak into `cruises.name` from unfinished test tear-down. Removes
// only tokens that appear as bullet-separated suffixes so real
// traveller-authored names like "The Certosa Cruise" are preserved.
const _cleanName = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  let out = raw;
  // Repeatedly peel off " · CERT-xxxxxx", " · TEST-xxxxxx", "  UUID"
  // and unbounded 8-4-4-4-12 uuid tails at the end of the string.
  const suffixPatterns = [
    /\s*[·|]\s*(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{4}\s*$/i,
    /\s*[·\-–—|]\s*CERT-[0-9a-f]{4,12}\s*$/i,
    /\s*[·\-–—|]\s*TEST-[0-9a-zA-Z_-]+\s*$/i,
    /\s*[·\-–—|]\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/i,
    /\s*[·\-–—|]\s*voy_[0-9a-f]{6,}\s*$/i,
  ];
  let changed = true;
  while (changed) {
    changed = false;
    for (const rx of suffixPatterns) {
      const trimmed = out.replace(rx, "");
      if (trimmed !== out) { out = trimmed; changed = true; }
    }
  }
  return out.trim();
};

// Format a date range using the canonical calendar-date helpers.
// Falls back gracefully when only one endpoint is available.
const _formatRange = (embark, disembark) => {
  const e = toCalendarDate(embark);
  const d = toCalendarDate(disembark);
  if (!e && !d) return "";
  if (e && d) {
    // "Oct 25 – Nov 1, 2026" pattern
    return formatCalendarRange(e, d, "medium");
  }
  return formatCalendarDate(e || d, "medium");
};

const _nights = (embark, disembark) => {
  const e = toCalendarDate(embark);
  const d = toCalendarDate(disembark);
  if (!e || !d) return null;
  const [y1, m1, d1] = e.split("-").map(Number);
  const [y2, m2, d2] = d.split("-").map(Number);
  const t1 = Date.UTC(y1, m1 - 1, d1);
  const t2 = Date.UTC(y2, m2 - 1, d2);
  const n = Math.round((t2 - t1) / 86400000);
  return n > 0 ? n : null;
};

/**
 * formatVoyageIdentity({ name, ship_name, embark_date, disembark_date, cruise_line })
 * → { primary, secondary, dateRange, nights }
 *
 * `primary`   — cruise identity headline (cleaned voyage name, fallback ship)
 * `secondary` — one-line context: ship · dates · nights (whatever is available)
 * `dateRange` — just the date range for cases that render it standalone
 * `nights`    — integer or null; useful for "N nights" chips
 */
export function formatVoyageIdentity({
  name,
  ship_name,
  embark_date,
  disembark_date,
  cruise_line,
} = {}) {
  const cleaned = _cleanName(name);
  const primary = cleaned || ship_name || "Your cruise";
  const dateRange = _formatRange(embark_date, disembark_date);
  const nights = _nights(embark_date, disembark_date);
  const secondaryBits = [
    ship_name && ship_name !== cleaned ? ship_name : null,
    dateRange || null,
    nights != null ? `${nights} nt${nights === 1 ? "" : "s"}` : null,
    cruise_line && !ship_name ? cruise_line : null,
  ].filter(Boolean);
  return {
    primary,
    secondary: secondaryBits.join(" · "),
    dateRange,
    nights,
  };
}

/** Traveller-facing composer for a workspace-hero subtitle. Never
 * repeats the cruise/voyage name — that lives in the Cruise Selector. */
export function heroSubtitle({ ship_name, embark_date, disembark_date } = {}) {
  const dateRange = _formatRange(embark_date, disembark_date);
  const bits = [ship_name || null, dateRange || null].filter(Boolean);
  return bits.join(" · ");
}

export default formatVoyageIdentity;
